import * as z from "zod";

import { SHAPEFILE_MATCH_MODE } from "../groupBy";

export const NormalizeBySettingSchema = z.object({
  per: z.number(),
  field: z.string(),
  display: z.string().optional(),
  unit: z.string(),
  defaultUnit: z.string().optional(),
  units: z.record(z.string(), z.string()).optional(),
  useMultilinearNormalization: z.boolean(),
  threshold: z.number(),
  lowerScalar: z.number(),
  higherScalar: z.number(),
  useNormalizeBy: z.boolean().optional(),
  displayName: z.string().optional()
});

export type NormalizeBySettingType = z.infer<typeof NormalizeBySettingSchema>;

export const NormalizationParamsSchema = z.object({
  useNormalizeBy: z.boolean(),
  globalNormalizeBy: NormalizeBySettingSchema.omit({ useNormalizeBy: true })
});

export type NormalizationParamsType = z.infer<typeof NormalizationParamsSchema>;

export const ForecastTrumpParamsSchema = z.object({
  forecastSources: z.array(z.string()),
  userForecastFolderId: z.string(),
  syncJob: z.string().optional(),
  syncJobRescat: z.string().optional(),
  userForecastRescat: z.string().optional()
});

export type ForecastTrumpParamsType = z.infer<typeof ForecastTrumpParamsSchema>;

export const DataQueryParamsSchema = z.object({
  pdenSource: z.number(),
  pageSize: z.number(),
  pageIndex: z.number(),
  sortColumn: z.string().optional(),
  sortDirection: z.string().optional(),
  entityKind: z.string().optional()
});

export type DataQueryParamsType = z.infer<typeof DataQueryParamsSchema>;

// PerformanceDateRange schema for validation. The type is defined in models/groupBy.ts
export const PerformanceDateRangeSchema = z.object({
  startDate: z.string(),
  endDate: z.string()
});

export const GroupModeRequestSchema = z.object({
  property: z.string(),
  bin: z.any().optional(),
  aggregation: z.number().optional(),
  wellView: z.boolean().optional()
});

export type GroupModeRequestType = z.infer<typeof GroupModeRequestSchema>;

export const FetchDataRequestSchema = z.object({
  filterId: z.string(),
  fields: z.array(z.string()),
  entityKind: z.string().optional(),
  outputFormatType: z.literal("JSON"),
  defaultSignificantFigures: z.number().optional(),
  useStrictRounding: z.boolean().optional(),
  normalization: NormalizeBySettingSchema.extend({
    useNormalizeBy: z.boolean()
  }).optional(),
  forecastTrumpingSettings: ForecastTrumpParamsSchema.optional(),
  groupMode: GroupModeRequestSchema.optional(),
  performanceDateRange: PerformanceDateRangeSchema.optional(),
  // Kept for compatibility with a service that still reads it. Nothing in the
  // UI sets it: a shapefile field is always fetched the intersecting way, and
  // "contained within" lives on the filter predicate instead.
  shapefileMatchMode: z
    .enum([SHAPEFILE_MATCH_MODE.Intersecting, SHAPEFILE_MATCH_MODE.ContainedWithin])
    .optional()
});

export type FetchDataRequestType = z.infer<typeof FetchDataRequestSchema>;

// NOTE: The gRPC DataResponse proto includes an `is_group_mode: bool` field
// (REQ-GRPC-WL-002) consumed by internal chart/map gRPC services. The HTTP
// POST /api/v1/data response body is a flat Record<column, string[]> of column
// values — `is_group_mode` is NOT serialised into the JSON body and is not
// present here. If the HTTP layer ever needs to expose this sentinel, a wrapper
// object with a dedicated `isGroupMode` key would be required.
export const FetchDataResponseSchema = z.record(z.string(), z.array(z.string()));

export type FetchDataResponseType = z.infer<typeof FetchDataResponseSchema>;

export function validateFetchDataResponse(data: unknown): FetchDataResponseType {
  return FetchDataResponseSchema.parse(data);
}

export function safeParseFetchDataResponse(data: unknown) {
  return FetchDataResponseSchema.safeParse(data);
}
